import { useState } from "react";
import type { AdminUser } from "../api";
import { useAuth } from "../auth";
import { Card, ErrorNote, Spinner, usd, useLoad } from "../ui";
import { BlocksCard, NoticesCard, PolicyCard, SharingCard } from "./AdminLegal";

export function AdminPage() {
  return (
    <div className="stack">
      <div className="page-head">
        <h1>Beheer</h1>
      </div>
      <PlatformCard />
      <NoticesCard />
      <UsersCard />
      <PolicyCard />
      <SharingCard />
      <BlocksCard />
      <UsageCard />
    </div>
  );
}

function PlatformCard() {
  const { api } = useAuth();
  const platform = useLoad(() => api.platform(), [api]);
  const [budget, setBudget] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function update(body: Parameters<typeof api.updatePlatform>[0]) {
    setError(null);
    try {
      platform.setData(await api.updatePlatform(body));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function reap() {
    const reaped = await api.reap();
    setMsg(`${reaped.length} vastgelopen job(s) opgeruimd.`);
  }

  if (platform.error) return <ErrorNote error={platform.error} />;
  if (!platform.data) return <Spinner />;
  const p = platform.data;
  const stopped = !p.extraction_enabled || p.emergency_stop_env;
  return (
    <Card className={stopped ? "card-alert" : ""}>
      <h2>Kosten & noodrem</h2>
      <dl className="facts-grid">
        <dt>Verwerking</dt>
        <dd>
          {p.emergency_stop_env
            ? "Gestopt via FABULA_EMERGENCY_STOP"
            : p.extraction_enabled
              ? "Actief"
              : "Gestopt (noodrem)"}
        </dd>
        <dt>Deze maand</dt>
        <dd>
          {usd(p.month_spend_usd)} van {usd(p.monthly_budget_usd)} (resterend{" "}
          {usd(p.month_remaining_usd)})
        </dd>
      </dl>
      <div className="row">
        <button
          className={`btn ${p.extraction_enabled ? "btn-danger" : ""}`}
          onClick={() => void update({ extraction_enabled: !p.extraction_enabled })}
        >
          {p.extraction_enabled ? "Noodrem: alle verwerking stoppen" : "Verwerking hervatten"}
        </button>
        <form
          className="row"
          onSubmit={(e) => {
            e.preventDefault();
            const n = Number(budget);
            if (budget && n >= 0) void update({ monthly_budget_usd: n });
          }}
        >
          <input
            type="number"
            min="0"
            step="1"
            placeholder="Maandbudget ($)"
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            aria-label="Maandbudget in dollar"
          />
          <button className="btn btn-ghost" type="submit">
            Opslaan
          </button>
        </form>
        <label className="check">
          <input
            type="checkbox"
            checked={p.off_peak_only}
            onChange={(e) => void update({ off_peak_only: e.target.checked })}
          />
          <span>Alleen in daluren verwerken (halve prijs; jobs worden ingepland)</span>
        </label>
        <button className="btn btn-ghost" onClick={() => void reap()}>
          Vastgelopen jobs opruimen
        </button>
      </div>
      {msg && <p className="muted">{msg}</p>}
      <ErrorNote error={error} />
    </Card>
  );
}

function UsersCard() {
  const { api } = useAuth();
  const users = useLoad(() => api.adminUsers(), [api]);
  const [error, setError] = useState<string | null>(null);

  async function run(fn: () => Promise<AdminUser>) {
    setError(null);
    try {
      await fn();
      users.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  if (users.error) return <ErrorNote error={users.error} />;
  if (!users.data) return <Spinner />;
  const order = { pending: 0, approved: 1, blocked: 2 } as const;
  const rows = [...users.data].sort((a, b) => order[a.status] - order[b.status]);
  return (
    <Card>
      <h2>Gebruikers</h2>
      <ErrorNote error={error} />
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Gebruiker</th>
              <th>Status</th>
              <th>Land</th>
              <th>Credits</th>
              <th title="Boeken verwijderd na een gegronde melding">Meldingen</th>
              <th>Actie</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id}>
                <td>
                  {u.email ?? u.display_name ?? u.id.slice(0, 8)}
                  {u.is_admin && <span className="muted small"> (beheerder)</span>}
                </td>
                <td>{u.status}</td>
                <td>{u.country ?? "—"}</td>
                <td>{u.credits_remaining}</td>
                <td className={u.takedowns >= 2 ? "danger" : ""}>{u.takedowns}</td>
                <td>
                  <CreditAction user={u} onRun={run} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function CreditAction({
  user,
  onRun,
}: {
  user: AdminUser;
  onRun: (fn: () => Promise<AdminUser>) => Promise<void>;
}) {
  const { api } = useAuth();
  const [credits, setCredits] = useState(3);
  if (user.status === "blocked") return null;
  return (
    <div className="row">
      <select
        value={credits}
        onChange={(e) => setCredits(Number(e.target.value))}
        aria-label="Aantal credits"
      >
        {[1, 2, 3, 4, 5].map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </select>
      {user.status === "pending" ? (
        <button className="btn small" onClick={() => void onRun(() => api.approve(user.id, credits))}>
          Goedkeuren
        </button>
      ) : (
        <button
          className="btn btn-ghost small"
          onClick={() => void onRun(() => api.grant(user.id, credits))}
        >
          Credits geven
        </button>
      )}
      {!user.is_admin && (
        <button
          className="btn btn-ghost small btn-danger"
          onClick={() => void onRun(() => api.block(user.id))}
        >
          Blokkeren
        </button>
      )}
    </div>
  );
}

function UsageCard() {
  const { api } = useAuth();
  const usage = useLoad(() => api.usage(), [api]);
  if (usage.error) return <ErrorNote error={usage.error} />;
  if (!usage.data) return <Spinner />;
  return (
    <Card>
      <h2>LLM-verbruik (31 dagen)</h2>
      {usage.data.length === 0 ? (
        <p className="muted">Nog geen verbruik.</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Dag</th>
                <th>Model</th>
                <th className="num">Calls</th>
                <th className="num">Prompt</th>
                <th className="num">Cache</th>
                <th className="num">Output</th>
                <th className="num">Kosten</th>
              </tr>
            </thead>
            <tbody>
              {usage.data.map((u) => (
                <tr key={`${u.day}-${u.model}`}>
                  <td>{u.day.slice(0, 10)}</td>
                  <td>{u.model}</td>
                  <td className="num">{u.calls}</td>
                  <td className="num">{u.prompt_tokens.toLocaleString("nl-NL")}</td>
                  <td className="num">{u.cached_prompt_tokens.toLocaleString("nl-NL")}</td>
                  <td className="num">{u.completion_tokens.toLocaleString("nl-NL")}</td>
                  <td className="num">{usd(u.cost_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
